// ExportCard.tsx — downloads: INRIA-3DGS-compatible PLY of the object or the
// whole scene, and the native OSPL snapshot. A scan with guided segments also
// offers every segment as its own PLY, and all of them as one zip (a PLY per
// segment plus a segments.json manifest). The capture video (§15.6) is
// offered once the server holds all of it; until then its state is noted.
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { BsDownload } from 'react-icons/bs';
import { urls } from '../api';
import type { Scan, SegmentInfo, VideoInfo } from '../api';
import { segmentColor } from './guide';

export interface ExportCardProps {
  scan: Scan;
  segments?: readonly SegmentInfo[] | null; // applied counts, when known
}

// videoNote describes a capture video that cannot be downloaded (yet), or
// one cut short by the phone's unsent-bytes cap.
function videoNote(v: VideoInfo | undefined): string {
  if (!v) return 'No capture video stored.';
  if (v.state === 'uploading') return 'Capture video still uploading from the phone.';
  if (v.state === 'partial') return `Capture video incomplete (${v.missing ?? 0} chunks missing${v.truncated ? ', phone memory cap hit' : ''}).`;
  if (v.truncated) return 'Capture video ends early: the phone hit its upload memory cap.';
  return '';
}

// videoExt is the download extension for the stored file.
function videoExt(v: VideoInfo): string {
  const m = v.file?.match(/\.(\w+)$/);
  return m ? m[1] : 'webm';
}

export function ExportCard({ scan, segments }: ExportCardProps) {
  const disabled = !scan.has_splats && !scan.has_seed;
  const base = scan.name.replace(/[^\w.-]+/g, '_') || scan.id;
  const guideSegs = scan.guide?.segments ?? [];
  const counts = new Map((segments ?? []).map((s) => [s.id, s.count]));
  return (
    <Card>
      <Card.Header>Export</Card.Header>
      <Card.Body className="small text-body-secondary">
        PLY opens in any 3DGS viewer (SuperSplat, Polycam, gsplat). Coordinates are metres, Z up, origin at the reference marker.
        {videoNote(scan.video) && <div className="mt-2">{videoNote(scan.video)}</div>}
        {guideSegs.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-1 mt-2">
            <Button size="sm" variant="outline-primary" as="a" href={urls.segmentsZip(scan.id)} download={`${base}-segments.zip`} disabled={disabled}>
              <BsDownload className="me-1" />
              Download {guideSegs.length} segments (zip)
            </Button>
            {guideSegs.map((s, i) => (
              <Button
                key={s.id}
                size="sm"
                variant="outline-secondary"
                as="a"
                href={urls.segmentPly(scan.id, s.id)}
                download={`${base}-${s.name.replace(/[^\w.-]+/g, '_') || `segment-${s.id}`}.ply`}
                disabled={disabled || counts.get(s.id) === 0}
                title={counts.has(s.id) ? `${(counts.get(s.id) ?? 0).toLocaleString()} splats` : undefined}
              >
                <span aria-hidden className="d-inline-block rounded-circle me-1" style={{ width: '0.6rem', height: '0.6rem', background: segmentColor(i) }} />
                <BsDownload className="me-1" />
                {s.name}
              </Button>
            ))}
          </div>
        )}
      </Card.Body>
      <Card.Footer className="d-flex flex-wrap gap-1">
        <Button size="sm" variant="outline-primary" as="a" href={urls.exportPly(scan.id, ['object'])} download={`${base}-object.ply`} disabled={disabled}>
          <BsDownload className="me-1" />
          Download Object PLY
        </Button>
        <Button size="sm" variant="outline-primary" as="a" href={urls.exportPly(scan.id)} download={`${base}.ply`} disabled={disabled}>
          <BsDownload className="me-1" />
          Download PLY
        </Button>
        <Button size="sm" variant="outline-secondary" as="a" href={urls.splats(scan.id)} download={`${base}.ospl`} disabled={disabled}>
          <BsDownload className="me-1" />
          Download OSPL
        </Button>
        {scan.video?.state === 'complete' && (
          <Button size="sm" variant="outline-secondary" as="a" href={urls.video(scan.id)} download={`${base}.${videoExt(scan.video)}`}>
            <BsDownload className="me-1" />
            Download video ({(scan.video.bytes / 1048576).toFixed(0)} MB)
          </Button>
        )}
      </Card.Footer>
    </Card>
  );
}
